import {evite} from 'evite';
import {action, computed, observable, observe} from 'mobx';
import {history as browserHistory} from '../../history';
import {parseParams} from '~/common/utils';
import {SearchSuggestionModel} from '~/common/models/SearchSuggestionModel';

export class ChangeDesignStore {
  @observable accessor enabled = false;

  @observable accessor isDrawerOpen = false;

  /**
   * A list of all categories. Does not change.
   *
   * @type {Array}
   */
  categories = [];

  /**
   * This is the current category used for displaying templates
   */
  @observable accessor category = {
    categoryId: null,
    displayName: null,
    eventType: null,
  };

  /**
   * The list of templates we are currently displaying in change design.
   * The source of templates may be the `category` or may be from a user search.
   *
   * If search, `search` will be non-empty.
   *
   * @type {Array}
   */
  @observable accessor templates = [];

  /**
   * The complete list of templates that we are iterating over
   * @type {Array}
   */
  allTemplates = [];

  @observable accessor searchType = SearchSuggestionModel.TYPE_CATEGORY;

  @observable accessor searchFound = false;

  @observable accessor searchTerm = '';

  @observable accessor maxPages = 0;

  /**
   * The max number of items per page
   * @type {number}
   */
  @observable accessor maxItemsPerPage = 5;

  /**
   * The current page we are on
   * @type {number}
   */
  @observable accessor pageIndex = 0;

  /**
   * True if there were no template results of the last operation
   * @type {boolean}
   */
  @observable accessor noResults = false;

  constructor({store, enabled, category_list}) {
    this.store = store;

    // pre-populate the templates list with the template supplied
    if (evite && evite.constants && evite.constants.TEMPLATE)
      this.allTemplates = [evite.constants.TEMPLATE];
    this.enabled = enabled;
    this.categories = category_list || window.category_list || [];
    this.maxItemsPerPage = 5;

    const {setBrowserHistoryFromStore, setStoreFromQuery} = this;
    if (process.env.NODE_ENV !== 'test') {
      // don't debounce under test because it makes it harder to test
      this.setStoreFromQuery = evite.debounce(setStoreFromQuery, 200, {
        leading: true,
      });
      this.setBrowserHistoryFromStore = evite.debounce(setBrowserHistoryFromStore, 200);
    }

    // find the category name on load
    const categoryName = this.getOriginalCategoryName();
    this.setCategoryByName(categoryName);

    observe(this, 'enabled', this.handleEnabledChange);

    observe(this.store.templateStore.template, 'eventType', this.setBrowserHistoryFromStore);
    observe(this.category, 'categoryId', this.setBrowserHistoryFromStore);
    observe(this.store.event, 'event_type', this.setBrowserHistoryFromStore);
    observe(this.store.templateStore.template, 'name', this.setBrowserHistoryFromStore);
  }

  /**
   * find the template name
   */
  _getDesignIndexByName(name) {
    if (!name) {
      return -1;
    }

    const templates = this.allTemplates;
    for (let i = 0; i < templates.length; ++i) {
      const model = templates[i];
      if (model.name === name) {
        return i;
      }
    }

    return -1;
  }

  /**
   * The current design within the templates
   * @type {number}
   */
  @computed
  get designIndex() {
    return this._getDesignIndexByName(this.store.templateStore.template.name);
  }

  @action
  setPageIndex(newPageIndex) {
    if (newPageIndex >= this.maxPages) {
      newPageIndex = 0;
    }
    if (newPageIndex < 0) {
      newPageIndex = this.maxPages - 1;
    }

    if (this.pageIndex !== newPageIndex || !this.templates.length) {
      this.templates = this.allTemplates.slice(
        newPageIndex * this.maxItemsPerPage,
        (newPageIndex + 1) * this.maxItemsPerPage
      );
      this.pageIndex = newPageIndex;
    }
  }

  nextPage() {
    this.setPageIndex(this.pageIndex + 1);
  }

  previousPage() {
    this.setPageIndex(this.pageIndex - 1);
  }

  @computed
  get hasNextDesign() {
    return this.designIndex < this.allTemplates.length - 1;
  }

  /**
   * helper for nextDesign and previousDesign - update the observed properties to make the
   * selected template the current index, rotate the slider if needed
   *
   * @private
   */
  _setTemplateIndex(index) {
    const template = this.allTemplates[index];
    this.store.templateStore.setTemplate(template);
    const newPageIndex = Math.floor(index / this.maxItemsPerPage) || 0;
    this.setPageIndex(newPageIndex);
  }

  @action
  nextDesign() {
    const {designIndex, hasNextDesign} = this;
    if (designIndex === -1 || !hasNextDesign) {
      this._setTemplateIndex(0);
    } else if (hasNextDesign) {
      this._setTemplateIndex(designIndex + 1);
    }
  }

  @action
  previousDesign() {
    const {designIndex} = this;

    if (designIndex > 0) {
      this._setTemplateIndex(designIndex - 1);
    } else {
      this._setTemplateIndex(this.allTemplates.length - 1);
    }
  }

  handleEnabledChange = (change) => {
    if (change.newValue) {
      this.setStoreFromQuery();
    }
  };

  @action
  setStoreFromQuery(location = '') {
    location = location || this.getLocation();
    const search = location.search.slice(1);
    const params = parseParams(search);

    if (!this.store.event.id) {
      const {template} = this.store.templateStore;
      const templateName = params.template || 'general_dyo';
      const categoryName = params.c || 'featured';
      this.isDrawerOpen = Boolean(
        window.system_generated || (params && 'expand_gallery' in params)
      );

      this.setCategoryByName(categoryName);
      this.setTemplateByName(templateName);
    }
  }

  getTemplateByName(name) {
    for (const template of this.store.changeDesignStore.allTemplates) {
      if (template.name === name) return template;
    }
    return null;
  }

  /**
   * given the template name, find it in the change design store and set it as the current template
   * @param name
   */
  @action
  setTemplateByName(name) {
    if (name === this.store.templateStore.template.name) return;

    // Revision templates are stored as templatename:revision_id but we just use the name
    if (name.indexOf(':') >= 0) name = name.split(':')[0];

    const template = this.getTemplateByName(name);
    if (!template) throw new Error(`Unable to find template: ${name}`);

    this.store.templateStore.setTemplate(template);
  }

  get categoryName() {
    return this.category.categoryId;
  }

  /**
   * given a category name, find it in the categories list and set it to current
   *
   * @param name the categoryId
   */
  @action
  setCategoryByName(name) {
    for (const cat of this.categories) {
      if (cat.categoryId === name) {
        this.setCategory(cat);
        return;
      }
    }

    if (name !== 'featured') this.setCategoryByName('featured');
  }

  /**
   * change the category
   * @param category
   */
  @action
  setCategory(category) {
    const categoryName = category.categoryId;
    if (!categoryName || categoryName === this.categoryName) {
      return Promise.resolve();
    }
    this.searchType = SearchSuggestionModel.TYPE_CATEGORY;

    // the category must be set here or there is a race on startup
    // and multiple requests might be sent

    // also, we have to set properties or the observers are not triggered
    Object.assign(this.category, category);
    this.searchTerm = '';
    this.searchFound = true;
    this.noResults = false;

    return this.store.templateStore
      .fetchTemplateListByName(categoryName)
      .then(
        action((templateList) => {
          if (categoryName !== this.categoryName) {
            // another set request must have changed this before the ajax response
            return;
          }
          templateList = templateList || [];
          this.searchTerm = '';
          this.searchFound = templateList.length > 0;
          this.noResults = templateList.length < 1;
          this._setAllTemplates(templateList);
          if (this.noResults) return;

          // see if we can find the current template in the new data
          let index = this._getDesignIndexByName(this.store.templateStore.template.name);
          if (index < 0) {
            index = 0;
          }
          const newPageIndex = Math.floor(index / this.maxItemsPerPage) || 0;
          this.templates = [];
          this.setPageIndex(newPageIndex);
        })
      )
      .catch(evite.logError);
  }

  getOriginalCategoryName() {
    return window.initial_evite_event
      ? window.initial_evite_event.tracking_data.gallery_category || 'featured'
      : 'featured';
  }

  /**
   * reset the change design components to the original state
   */
  @action
  reset() {
    const {categories} = this;

    this.searchTerm = '';
    let categoryPromise = Promise.resolve();
    const categoryName = this.getOriginalCategoryName();
    for (let i = 0; i < categories.length; ++i) {
      const cat = categories[i];
      if (cat.categoryId === categoryName) {
        categoryPromise = this.setCategory(cat);
        break;
      }
    }

    categoryPromise.then(() => {
      const {allTemplates} = this;
      let templateIndex = 0;
      for (let i = 0; i < allTemplates.length; ++i) {
        const template = allTemplates[i];
        if (template.name === evite.constants.TEMPLATE.name) {
          templateIndex = i;
          break;
        }
      }

      this._setTemplateIndex(templateIndex);
    });
  }

  setBrowserHistoryFromStore = () => {
    const {template} = this.store.templateStore;
    const location = this.getLocation();

    let eventType = this.store.event.event_type || template.eventType || '';
    if (!eventType || eventType.toLowerCase() === 'none') {
      eventType = '';
    }

    const params = parseParams(location.search.slice(1));
    const updatedParams = {...params};
    updatedParams.event_type = eventType;
    updatedParams.c = this.category.categoryId;
    updatedParams.template = template.name;

    updatedParams.queryType = this.searchType;
    updatedParams.query = encodeURIComponent(this.searchTerm);

    const hash = (location.hash && `#${location.hash}`) || '';
    const parser = document.createElement('a');
    parser.href = location.href;
    const paramsString = evite.param(updatedParams, false);
    this.replaceBrowserHistory(`${parser.pathname}?${paramsString}${hash}`);
  };

  getLocation() {
    return window.location;
  }

  replaceBrowserHistory(url) {
    browserHistory.replace(url);
  }

  @action
  _setAllTemplates(templates) {
    this.maxPages = !templates.length ? -1 : Math.ceil(templates.length / this.maxItemsPerPage);

    this.allTemplates = templates;
    this.templates = templates.slice(0, this.maxItemsPerPage);

    this.pageIndex = 0;
  }

  searchBySearchSuggestionModel = async (searchSuggestionModel) => {
    if (!searchSuggestionModel) {
      return Promise.reject('Undefined search template model');
    }

    const searchType = searchSuggestionModel.type;
    const searchId = searchSuggestionModel.id;
    const searchName = `${searchType}-${searchId}`;
    this._currentSearchName = searchName;

    if (searchType === 'category') {
      this.setCategoryByName(searchId);
    } else {
      const {found, templates} = await this.store.templateStore.fetchTemplateListBySearch(searchId);
      this.setBrowserHistoryFromStore();

      if (this._currentSearchName === searchName) {
        action(() => {
          this.searchType = searchType;
          this.searchFound = found;
          // there will always be results because the server replaces it with the dyo
          // category. but if we want to show the "no results" message, set this flag true.
          this.noResults = !found;
          this.searchTerm = searchSuggestionModel.text;
          this.searchType = 'suggestion';

          this.category = {};
          this._setAllTemplates(templates);
        })();
      }
    }
  };
}
